import React, { useEffect, useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useFormik } from "formik";
import * as Yup from "yup";
import Spinner from "../loading/spinner";
import { convertTo24Hour, formatTime } from "../../specialMethod/getBetweenDate";
import { toggleState } from "../../specialMethod/toggleState";
import { InputField, SelectField } from "./dashboard_function/addSchedule_function";

// localStorage helpers
const getSchedules = () => JSON.parse(localStorage.getItem("doctorSchedules")) || [];
const saveSchedules = (schedules) => localStorage.setItem("doctorSchedules", JSON.stringify(schedules));
const generateId = () => "schedule_" + Date.now() + "_" + Math.random().toString(36).substring(2, 8);

const AddScheduleForDoctor = ({ reset, updatedSchedule }) => {
  const [state, setState] = useState({
    loadingSpinner: false,
    timeSlots: [],
    checkSchedule: false,
    collectScheduleData: [],
    short: false,
    filteredEndTimes: [],
  });

  // Load existing schedules for conflict check
  useEffect(() => {
    const schedules = getSchedules();
    toggleState("collectScheduleData", schedules, setState);
  }, []);

  // Save or update schedule
  const saveSchedule = (values, isShort = false) => {
    const allSchedules = getSchedules();
    let updatedSchedules = [];

    if (updatedSchedule) {
      // Edit mode
      updatedSchedules = allSchedules.map((sch) =>
        sch.id === updatedSchedule.id
          ? { ...sch, ...values, isShort, updatedAt: new Date().toISOString() }
          : sch
      );
    } else {
      // New schedule
      const newSchedule = {
        id: generateId(),
        ...values,
        isShort,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      updatedSchedules = [...allSchedules, newSchedule];
    }

    saveSchedules(updatedSchedules);
    // Update local state (for conflict check)
    toggleState("collectScheduleData", updatedSchedules, setState);
    return updatedSchedules;
  };

  const handleSubmit = (values) => {
    setState((prev) => ({ ...prev, loadingSpinner: true }));
    saveSchedule(values, state.short);
    toast.success(updatedSchedule ? "Schedule updated!" : "Schedule added!");
    setState((prev) => ({ ...prev, loadingSpinner: false }));
    // Close modal – parent will refresh
    reset();
  };

  const formik = useFormik({
    initialValues: {
      purpose: updatedSchedule?.purpose || "",
      clinicName: updatedSchedule?.clinicName || "",
      startTime: updatedSchedule?.startTime || "12:00:00",
      endTime: updatedSchedule?.endTime || "12:00:00",
      stDate: updatedSchedule?.stDate || "",
      endDate: updatedSchedule?.endDate || "",
    },
    validateOnBlur: true,
    validationSchema: Yup.object().shape({
      purpose: Yup.string().required("Required"),
      clinicName: Yup.string().required("Required"),
      startTime: Yup.string().required("Required"),
      endTime: Yup.string().required("Required"),
      stDate: Yup.string().required("Required"),
      endDate: Yup.string().required("Required"),
    }),
    onSubmit: (values) => handleSubmit(values),
  });

  // Generate time slots (15-min intervals)
  const timeSlots = useMemo(() => {
    const slots = [];
    const start = new Date("1970-01-01T00:00:00");
    const end = new Date("1970-01-01T23:45:00");
    while (start <= end) {
      slots.push(formatTime(start));
      start.setMinutes(start.getMinutes() + 15);
    }
    return slots;
  }, []);

  useEffect(() => {
    toggleState("timeSlots", timeSlots, setState);
    toggleState("filteredEndTimes", timeSlots, setState);
  }, [timeSlots]);

  // Filter end times based on start time
  useEffect(() => {
    const { startTime } = formik.values;
    if (startTime) {
      const startIndex = state.timeSlots.findIndex((t) => t === startTime);
      const filtered = state.timeSlots.slice(startIndex + 1);
      toggleState("filteredEndTimes", filtered, setState);
    } else {
      toggleState("filteredEndTimes", state.timeSlots, setState);
    }
  }, [formik.values.startTime, state.timeSlots]);

  // Conflict detection
  useEffect(() => {
    const { startTime, endTime, stDate, endDate } = formik.values;
    if (startTime && endTime && stDate && endDate && state.collectScheduleData) {
      const startDate = new Date(`${stDate}T${convertTo24Hour(startTime)}`);
      const endDateObj = new Date(`${endDate}T${convertTo24Hour(endTime)}`);
      const toCheck = state.collectScheduleData.filter(
        (sch) => !updatedSchedule || sch.id !== updatedSchedule.id
      );
      const conflict = toCheck.some((sch) => {
        const s = new Date(`${sch.stDate}T${sch.startTime}`);
        const e = new Date(`${sch.endDate}T${sch.endTime}`);
        return startDate < e && endDateObj > s;
      });
      if (state.checkSchedule !== conflict) {
        toggleState("checkSchedule", conflict, setState);
      }
    }
  }, [
    formik.values.startTime,
    formik.values.endTime,
    formik.values.stDate,
    formik.values.endDate,
    state.collectScheduleData,
    updatedSchedule,
  ]);

  const today = new Date().toISOString().split("T")[0];
  const fields = [
    { component: InputField, label: "Purpose", type: "text", name: "purpose", placeholder: "e.g., Consultation" },
    { component: InputField, label: "Location", type: "text", name: "clinicName", placeholder: "Clinic name" },
    { component: SelectField, label: "Start Time", name: "startTime", options: state.timeSlots },
    { component: SelectField, label: "End Time", name: "endTime", options: state.filteredEndTimes },
    { component: InputField, label: "Start Date", type: "date", name: "stDate", min: today },
    { component: InputField, label: "End Date", type: "date", name: "endDate", min: formik.values.stDate || today },
  ];

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-auto transform transition-all scale-100">
          <div className="flex items-center justify-between p-5 border-b border-gray-100">
            <h3 className="text-xl font-bold text-gray-800">
              {updatedSchedule ? "Edit Schedule" : "New Schedule"}
            </h3>
            <button onClick={reset} className="p-1 rounded-full hover:bg-gray-100 transition">
              <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <form onSubmit={formik.handleSubmit} className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {fields.map((field, i) => {
                const Comp = field.component;
                return (
                  <Comp
                    key={i}
                    label={field.label}
                    type={field.type}
                    name={field.name}
                    value={formik.values[field.name]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder={field.placeholder}
                    options={field.options}
                    error={formik.touched[field.name] && formik.errors[field.name]}
                    min={field.min}
                  />
                );
              })}
              <div className="col-span-2 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="shortEvent"
                  checked={state.short}
                  onChange={() => toggleState("short", !state.short, setState)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="shortEvent" className="text-sm text-gray-700">Short Event (skip conflict check)</label>
              </div>
            </div>
            {state.checkSchedule && (
              <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded">
                <p className="text-sm text-red-700">Time slot overlaps with existing schedule.</p>
              </div>
            )}
            <button
              type="submit"
              disabled={state.checkSchedule}
              className={`w-full py-3 px-4 rounded-xl font-semibold transition-all ${
                state.checkSchedule
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-indigo-600 hover:bg-indigo-700 text-white shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              }`}
            >
              {state.loadingSpinner ? "Saving..." : updatedSchedule ? "Update" : "Create"}
            </button>
          </form>
        </div>
      </div>
      <ToastContainer />
      {state.loadingSpinner && <Spinner />}
    </>
  );
};

export default AddScheduleForDoctor;